import React, { useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { makeStyles, Snackbar } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';

import {
  removeFileForLanguageAction,
  updateSubjectErrorAction,
  uploadFileForLanguageAction,
} from './context/message-composer-actions';

import { uploadToFileBucketViaPresignedUrl } from '../../api/file-upload-api-client';
import { FormattedMessageBody } from '../../messages/legacy-components/formatted-message-body';
import Message from '../legacy-message';
import { TransitionSlideUp } from '../transition-slide-up';

import {
  useMessageComposerDispatch,
  useMessageInputState,
} from './context/message-composer-context';
import { LanguagePreferenceBanner } from './language-preference-banner';
import { FilesDisplay } from './toolbar/files-display';
import { PersonalizeButton } from './toolbar/personalize-button';
import { TranslateButton } from './toolbar/translate-button';
import { UploadFileButton } from './toolbar/upload-file-button';
import { RichTextEditorInContext } from './wrappers/rich-text-editor-in-context';

import { checkSubjectForErrors } from './util/check-message-for-errors-and-warnings';
import {
  MAX_TRANSLATE_CHARACTER_LIMIT,
  TRANSLATE_CHARACTER_LIMIT_EXCEEDED_MESSAGE,
} from '../../messages/utils/use-sent-message-translation';
import { plainTextEditorConfig, richTextEditorConfig } from '../../util/ckeditor-config';
import { focusRichTextEditor } from '../../util/focus-rich-text-editor';
import { MESSAGE_TYPE } from '../../util/message-types';

const useStyles = makeStyles((theme) => ({
  errorOutline: {
    borderBottom: `2px solid ${theme.palette.error.main}`,
  },
}));

type MessageInputProps = {
  // ====== These are the props that are just the data passed from the multiple message composer ======
  orgId?: number;

  subject: string;

  /**
   * Whether the parent of this component is loading or not. Disabled the input when truthy
   */
  loading: boolean;

  // ====== These are configuration props that change user-displayed features ======

  /**
   * Configuration passed down from the parent. It controls whether or not this control should regain focus after
   * a message change - it's specifically to deal with the focus being lost when someone enters a personalized
   * variable
   */
  autofocus: boolean;

  /**
   * This controls whether or not the personalize functions are shown to the user. Another pass through from the
   * parent. Shown on truthy
   */
  canPersonalize: boolean;

  /**
   * Whether to include employee or job applicant language
   */
  recipientType?: ContactType;

  // ====== These are the handlers ======

  onSubjectChange: (language: string, subject: string) => void;

  //* ====== File upload toolbar plugin ====== */
  canUploadFile?: boolean;

  //* ====== Translation toolbar plugin ====== */
  /**
   * Enables the visibility of the translate toolbar button
   */
  canTranslate?: boolean;
  languagePreferenceBannerMessages?: Record<string, string>;
};

/**
 * This component is to wrap up some of the low level concerns and actually render the rich text editors.
 * One of the main motivations is to simply the interface to just be (language, message) instead of having to deal
 * with the rich text editors outright. This is the point where we also check for issues like string length and
 * missing subjects
 */
export const MessageInput = ({
  orgId,
  loading,
  subject,
  onSubjectChange,
  autofocus,
  canPersonalize,
  recipientType = 'employee',
  canUploadFile,
  canTranslate,
  // Can be removed after PROD-4039
  languagePreferenceBannerMessages,
}: MessageInputProps) => {
  const editorRef = useRef<{ editor: CKEditorInstance | null } | null>(null);

  const classes = useStyles();

  const disableTranslateButton =
    (editorRef?.current?.editor?.document?.$?.body?.innerText || '').length >
    MAX_TRANSLATE_CHARACTER_LIMIT;
  const disableTranslateButtonMessage = disableTranslateButton
    ? TRANSLATE_CHARACTER_LIMIT_EXCEEDED_MESSAGE
    : null;

  const [uploadErrorNotification, setUploadErrorNotification] = useState<{
    key: string;
    message: string;
  }>();

  const {
    languageCode,
    urlFileMappings,
    messageType,
    messageError,
    messageWarning,
    streamingHackText,
    isTranslating,
    subjectError,
    fileCount,
    disableSubject,
  } = useMessageInputState();
  const messageComposerDispatch = useMessageComposerDispatch();

  const focusTextEditor = (e: CKEditorEventInfo) => {
    if (autofocus) {
      focusRichTextEditor(e);
    }
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const error = checkSubjectForErrors(messageType, e.currentTarget.value);
    messageComposerDispatch(
      updateSubjectErrorAction({
        subjectError: error,
        languageCode,
      }),
    );

    onSubjectChange(languageCode, e.currentTarget.value);
  };

  const handleSubjectTranslation = (newSubject: string) => {
    onSubjectChange(languageCode, newSubject);
  };

  const onUploadFile = async (file: File) => {
    if (!orgId) {
      return '';
    }

    const resultLink = await uploadToFileBucketViaPresignedUrl(file, orgId).catch(() => {
      // no-op
    });
    if (!resultLink) {
      setUploadErrorNotification({
        key: `failed-upload-${file.name}`,
        message: 'File upload failed, please try again',
      });
      return '';
    }

    messageComposerDispatch(
      uploadFileForLanguageAction({
        languageCode,
        fileName: file.name,
        resultLink,
      }),
    );

    return `{{{url${fileCount + 1}}}}`;
  };

  const onDeleteFileChipClick = (urlFileMapping: UrlFileMapping) => () => {
    messageComposerDispatch(removeFileForLanguageAction({ languageCode, urlFileMapping }));
  };

  const handleVariableSelect = async (variable: string) => {
    if (editorRef?.current?.editor?.insertText) {
      const { editor } = editorRef.current;
      editor.insertText(`${variable} `);
    }
  };

  const handleCloseUploadErrorNotification = () => {
    setUploadErrorNotification(undefined);
  };

  const buttonPosition =
    messageType === MESSAGE_TYPE.TEXT ? { top: 10, left: 50 } : { top: 10, left: 270 };

  const isTypeEmail = messageType === MESSAGE_TYPE.EMAIL;

  return (
    <>
      {messageWarning && <Message message={messageWarning} variant="warning" />}
      {languagePreferenceBannerMessages ? (
        <LanguagePreferenceBanner message={languagePreferenceBannerMessages[languageCode]} />
      ) : null}
      {isTypeEmail && !disableSubject && (
        <TextField
          id="subject"
          label="Subject"
          value={subject}
          onChange={handleSubjectChange}
          margin="dense"
          className="u-width-100"
          required
          error={!!subjectError}
          helperText={subjectError}
          disabled={loading}
          data-cy={`subject-input-${languageCode}`}
        />
      )}
      <FilesDisplay
        languageCode={languageCode}
        urlFileMappings={urlFileMappings}
        onDelete={onDeleteFileChipClick}
      />
      <div className={`u-position-relative ${messageError ? classes.errorOutline : ''}`}>
        <div
          style={{ position: 'absolute', ...buttonPosition }}
          className="u-display-flex u-gap-5px u-lineHeight-normal"
          data-cy={`editor-toolbar-${languageCode}`}
        >
          {canPersonalize && (
            <PersonalizeButton
              // TODO[PROD-3453]: use context
              messageType={messageType}
              onVariableSelect={handleVariableSelect}
              recipientType={recipientType}
              buttonDataCy={`personalize-${languageCode}`}
            />
          )}
          {canUploadFile && (
            <UploadFileButton
              languageCode={languageCode}
              disabled={loading || isTranslating}
              onVariableSelect={handleVariableSelect}
              urlFileMappings={urlFileMappings}
              onUploadFile={onUploadFile}
            />
          )}
          {canTranslate && (
            <TranslateButton
              isMessageSending={loading}
              subject={subject}
              onSubjectChange={handleSubjectTranslation}
              disabled={disableTranslateButton}
              disabledTooltip={disableTranslateButtonMessage}
            />
          )}
        </div>

        {isTypeEmail ? (
          // This fragment is important otherwise the rich text
          // toolbar does not load properly
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            <RichTextEditorInContext
              ref={editorRef}
              onInstanceReady={focusTextEditor}
              config={richTextEditorConfig}
              readOnly={loading}
            />
          </>
        ) : (
          <RichTextEditorInContext
            ref={editorRef}
            onInstanceReady={focusTextEditor}
            config={plainTextEditorConfig}
            readOnly={loading}
            injectCKEditorLineEndings
          />
        )}
      </div>
      {messageError && (
        <div className="u-color-red typo-subtitle" data-cy="message-input-error">
          {messageError}
        </div>
      )}
      {/* This portal exists because CKEditor has an issue where constantly
          setting the data results in jittery updates.
          See https://github.com/ckeditor/ckeditor4/issues/2659 for a fiddle
          to see the jittery problem in action */}
      {editorRef?.current?.editor?.document?.$?.body &&
        streamingHackText &&
        createPortal(
          <div
            style={{
              // Position moves this portal on top of the existing div
              position: 'absolute',
              // 20px is the margin. Text starts 20px down
              // from the top
              top: '20px',
              // it also respects the left margin but not the right
              marginRight: '20px',
              // ckeditor height is 200px height
              // 200 - top ^ = 180
              minHeight: '180px',
            }}
          >
            <FormattedMessageBody messageBody={streamingHackText} includesHTML />
          </div>,
          // without our div portal, the content of the body is just the editor
          // text
          editorRef.current.editor.document.$.body,
        )}
      {/* Since there are some contexts in which the MLMC is used outside of
       * Redux, we can't assume access to the Redux store in this component.
       * As such, we manually render the below snackbar when there's an error
       * uploading a file  */}
      {uploadErrorNotification && (
        <Snackbar
          {...uploadErrorNotification}
          open
          onClose={handleCloseUploadErrorNotification}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          autoHideDuration={5000}
          transitionDuration={{
            enter: 500,
            exit: 500,
          }}
          TransitionComponent={TransitionSlideUp}
        />
      )}
    </>
  );
};
